import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';
import React from 'react';
import PropTypes from 'prop-types';

import { useAppContext } from '../hooks/AppProvider';
import SpinnerLoader from './SpinnerLoader';

const StyledDiv = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
});

const Proxy = () => <div />;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ContentWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'reducedMotion',
})(({ reducedMotion }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  animation: reducedMotion ? 'none' : `${fadeIn} 1.2s ease-out forwards`,
}));

const AnimationContainer = ({ lazy }) => {
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(8);
  const { reducedMotion } = useAppContext();

  React.useEffect(() => {
    if (reducedMotion) {
      setContent(<Proxy />);
      setLoading(false);
      return undefined;
    }

    let abort = false;
    const controller = new AbortController();

    const lazyLoad = async () => {
      controller.abort();
      setLoading(true);
      try {
        const result = await lazy();
        if (!abort) {
          setContent(<result.Component />);
        }
      } finally {
        if (!abort) {
          setLoading(false);
        }
      }
    };

    ['pointermove', 'scroll'].forEach((eventName) => {
      window.addEventListener(eventName, lazyLoad, {
        once: true,
        passive: true,
        signal: controller.signal,
      });
    });

    return () => {
      abort = true;
      controller.abort();
    };
  }, [lazy, reducedMotion]);

  React.useEffect(() => {
    if (!loading) {
      return undefined;
    }

    setProgress(8);
    const interval = window.setInterval(() => {
      setProgress((currentProgress) => {
        const step = Math.max(1, Math.round((92 - currentProgress) * 0.12));
        return Math.min(92, currentProgress + step);
      });
    }, 160);

    return () => {
      window.clearInterval(interval);
    };
  }, [loading]);

  return (
    <StyledDiv>
      {content ? (
        <ContentWrapper reducedMotion={reducedMotion}>{content}</ContentWrapper>
      ) : (
        loading ? (
          <SpinnerLoader
            floating
            label="loading..."
            progress={progress}
            reducedMotion={reducedMotion}
          />
        ) : (
          <Proxy />
        )
      )}
    </StyledDiv>
  );
};

AnimationContainer.propTypes = {
  lazy: PropTypes.func.isRequired,
};

export default AnimationContainer;
